import { router } from '@inertiajs/vue3'

import type { CalloutIdentity, CalloutPageProps } from '@/domains/integrations/callout/callout.types'

const WIDGET_SRC = 'https://cdn.withcallout.com/widget.js'

/**
 * Append the widget script, and call `onReady` once it has published its API.
 *
 * `CalloutConfig` is set before the script is appended because the widget reads it
 * while it boots.
 *
 * A blocked or missing CDN warns rather than throws. The widget is a feedback tool,
 * so it may never take the admin panel down with it — but a proxy that swallows the
 * request is the likeliest failure here, and silence makes it unreadable.
 */
const loadWidget = (projectId: string, onReady: () => void): void => {
  window.CalloutConfig = { projectId }

  const script = document.createElement('script')

  script.src = WIDGET_SRC
  script.async = true
  script.addEventListener('load', onReady)
  script.addEventListener('error', () => {
    // Dev-only surface: production has no telemetry sink wired here yet, so
    // gating on DEV keeps consoles clean without swallowing the signal locally.
    if (import.meta.env.DEV) {
      console.warn(`withCallout did not load from ${WIDGET_SRC}`)
    }
  })

  document.body.appendChild(script)
}

/**
 * Join the name parts the way `User::getFullNameAttribute` does, or return nothing
 * when the user has neither.
 *
 * Composed here rather than read from `full_name`: that is an accessor the User model
 * does not append, so it never reaches the page.
 */
const toName = (first?: string | null, last?: string | null): string | undefined =>
  [first, last].filter(Boolean).join(' ') || undefined

/**
 * Describe the signed-in user for withCallout, or return nothing when there is none.
 *
 * The tenant travels as a trait so staging feedback says which tenant it came from.
 * User ids are UUIDs, so the raw id needs no tenant prefix to stay unique.
 */
const toIdentity = (props: CalloutPageProps): CalloutIdentity | undefined => {
  const user = props.auth?.user

  if (!user?.id) {
    return undefined
  }

  return {
    id: user.id,
    email: user.email ?? undefined,
    name: toName(user.first_name, user.last_name),
    traits: {
      tenant: props.tenant?.name,
      tenant_id: props.tenant?.id,
      plan: props.tenant?.plan
    }
  }
}

/**
 * Load the withCallout widget for the admin app and keep it told who is signed in.
 *
 * Two conditions, both required. The `callout.project_id` shared prop must carry a
 * value, so the widget is absent in every environment that leaves
 * `CALLOUT_PROJECT_ID` empty. And somebody must be signed in, so no script is
 * requested on login, password reset or two-factor — nobody on those screens is a
 * user we can collect feedback from.
 *
 * The script is therefore requested on the first page that has a user, which is the
 * initial load for a signed-in visitor and the sign-in visit for everyone else.
 *
 * Call this once in app.ts, passing the initial page props.
 */
export const installCallout = (initialProps: CalloutPageProps): void => {
  const projectId = initialProps.callout?.project_id

  if (!projectId) {
    return
  }

  let identifiedUserId: string | undefined
  let identity: CalloutIdentity | undefined
  let requested = false

  // Reads whatever the latest visit set, so a user who changes while the async script
  // is still arriving is identified as themselves rather than as their predecessor.
  const identify = (): void => {
    if (!identity || identity.id === identifiedUserId || !window.Callout) {
      return
    }

    identifiedUserId = identity.id
    window.Callout.identify(identity)
  }

  const sync = (props: CalloutPageProps): void => {
    identity = toIdentity(props)

    // Signing out clears the record, so the next sign-in identifies again even when it
    // is the same person. Holding the id would leave the widget attributing a second
    // person's feedback to whoever used the tab first.
    if (!identity) {
      identifiedUserId = undefined

      return
    }

    if (requested) {
      identify()

      return
    }

    requested = true
    loadWidget(projectId, identify)
  }

  sync(initialProps)

  // Signing in is an Inertia visit, not a page load, so app.ts never re-runs for it.
  // This covers that, and a switch to a different user in the same tab.
  router.on('success', event => sync(event.detail.page.props as CalloutPageProps))
}
